/* eslint-disable */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  Box, Grid, Typography, Pagination, Chip, MenuItem, Select, Skeleton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import useNetworkLoader from 'hooks/useNetworkLoader';
import { useLocation, useHistory } from 'react-router-dom';
import ConfigContext from 'Shared/ConfigContext';
import AnnouncementBanner from 'Shared/AnnouncementBanner';
import {
  setOpenPanierList,
} from 'Shared/ConfigContext/Actions';
import useQuery from 'hooks/useQuery';
import ReactPiwik from 'react-piwik';
import EmptyState from 'Shared/EmptyState';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ProductCard from 'Shared/ProductCard';
import { getBrands, getFamilies, getProductsData } from './CatalogServices';
import { getFilterCounts } from './SearchService';
import ProductDetail from './ProductDetail';
import AddPanierModal from './popup';
import { SORT_OPTIONS } from './constants';
import FilterSidebar from './FilterSidebar';
import SearchResults from './SearchResults';

/* ── SelectedPorduct — named export required by SubHeader.js ── */
export const SelectedPorduct = ({ product, onChangeProduct }) => {
  if (!product) return null;
  return (
    <ProductDetail
      product={product}
      stockDisponibleBouskoura={product?.stockBouskoura || 0}
      stockDisponibleDebOmar={product?.stockDerbOmar || 0}
      onHandleClickCard={onChangeProduct || (() => {})}
      showRemise
    />
  );
};

/* ── Main Catalog component ──────────────────────────────────── */
const Catalog = () => {
  const location = useLocation();
  const history = useHistory();
  const query = useQuery();
  const { callApi } = useNetworkLoader();

  const { dispatch, state: { paniers } } = useContext(ConfigContext);

  /* filters state — initialized from URL so back navigation restores them */
  const [familyFilters, setFamilyFilters] = useState(() => {
    const f = query.get('families');
    return f ? f.split(',').map(Number).filter(Boolean) : [];
  });
  const [brandFilters, setBrandFilters] = useState(() => {
    const b = query.get('brands');
    return b ? b.split(',') : [];
  });
  const [productFilters, setProductFilters] = useState(() => {
    const pf = query.get('filters');
    return pf ? pf.split(',') : [];
  });
  const [page, setPage] = useState(() => Number(query.get('page')) || 0);
  const [sortBy, setSortBy] = useState(() => query.get('sort') || 'reference_asc');
  const [priceMin, setPriceMin] = useState(() => query.get('priceMin') || '');
  const [priceMax, setPriceMax] = useState(() => query.get('priceMax') || '');
  const [appliedPriceMin, setAppliedPriceMin] = useState(() => query.get('priceMin') || '');
  const [appliedPriceMax, setAppliedPriceMax] = useState(() => query.get('priceMax') || '');

  /* products state */
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);

  /* families/categories state */
  const [allFamilies, setAllFamilies] = useState([]);

  /* brands state */
  const [allBrandsRaw, setAllBrandsRaw] = useState([]);

  /* filter counts state */
  const [filterCounts, setFilterCounts] = useState(null);
  const [filterCountsLoading, setFilterCountsLoading] = useState(false);
  const [searchCounts, setSearchCounts] = useState(null);

  const isCatalogVisible = !location.pathname.startsWith('/products/');
  const searchFromUrl = query.get('search') || location.state?.search || '';
  const searchVersion = location.state?.searchVersion;
  const lastCatalogSearch = useRef(searchFromUrl);

  if (isCatalogVisible) lastCatalogSearch.current = searchFromUrl;
  const search = isCatalogVisible ? searchFromUrl : lastCatalogSearch.current;


  /* sync filter state to URL so back navigation restores filters */
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (familyFilters.length) params.set('families', familyFilters.join(','));
    if (brandFilters.length) params.set('brands', brandFilters.join(','));
    if (productFilters.length) params.set('filters', productFilters.join(','));
    if (page > 0) params.set('page', String(page));
    if (sortBy !== 'reference_asc') params.set('sort', sortBy);
    if (appliedPriceMin) params.set('priceMin', appliedPriceMin);
    if (appliedPriceMax) params.set('priceMax', appliedPriceMax);
    const newSearch = params.toString();
    const currentNormalized = new URLSearchParams(location.search).toString();
    if (newSearch !== currentNormalized) {
      history.replace({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '', state: location.state });
    }
  }, [familyFilters, brandFilters, productFilters, page, sortBy, appliedPriceMin, appliedPriceMax]);

  /* load families once */
  useEffect(() => {
    callApi(
      () => getFamilies(),
      (data) => setAllFamilies(data || []),
      false,
    );
  }, []);

  /* load brands once */
  useEffect(() => {
    callApi(
      () => getBrands(''),
      (data) => setAllBrandsRaw(data || []),
      false,
    );
  }, []);

  /* load filter counts on filter change — skip in search mode (counts derived from search results) */
  useEffect(() => {
    if (!isCatalogVisible || search) return;
    const body = { families: familyFilters, brands: brandFilters };
    if (productFilters.includes('new')) body.isNew = true;
    if (productFilters.includes('available')) body.available = true;
    if (productFilters.includes('promo')) body.promo = true;
    if (appliedPriceMin) body.minPrice = Number(appliedPriceMin);
    if (appliedPriceMax) body.maxPrice = Number(appliedPriceMax);
    setFilterCountsLoading(true);
    callApi(
      () => getFilterCounts(body),
      (data) => { setFilterCounts(data); setFilterCountsLoading(false); },
      false,
    );
  }, [familyFilters, brandFilters, productFilters, search, appliedPriceMin, appliedPriceMax]);

  /* load products on filter / page / sort change */
  useEffect(() => {
    if (!isCatalogVisible || search) return;
    const body = { families: familyFilters, brands: brandFilters };
    if (search) body.search = search;
    if (productFilters.includes('new')) body.isNew = true;
    if (productFilters.includes('available')) body.available = true;
    if (productFilters.includes('promo')) body.promo = true;
    if (appliedPriceMin) body.minPrice = Number(appliedPriceMin);
    if (appliedPriceMax) body.maxPrice = Number(appliedPriceMax);

    const [sortField, sortDir] = sortBy.split('_');
    setProductsLoading(true);
    callApi(
      () => getProductsData(`page=${page}&size=20&sort=${sortField},${sortDir}`, body),
      (data) => {
        setProducts(data?.content || []);
        setTotalElements(data?.totalElements || 0);
        setTotalPages(data?.totalPages || 1);
        setProductsLoading(false);
      },
      false,
    );
  }, [page, sortBy, familyFilters, brandFilters, productFilters, search, appliedPriceMin, appliedPriceMax]);

  const handleAddToCart = (product) => {
    ReactPiwik.push(['trackEvent', 'Panier', 'Ajouter', product.reference]);
    dispatch(setOpenPanierList(true, product));
  };

  const handleOpenDetail = (product) => {
    history.push(`/products/${product.reference}`, { background: location, product });
  };

  const toggleFamilyFilter = (id) => {
    setPage(0);
    setFamilyFilters((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleBrandFilter = (brand) => {
    setPage(0);
    setBrandFilters((prev) =>
      prev.includes(brand) ? prev.filter((x) => x !== brand) : [...prev, brand],
    );
  };

  const toggleProductFilter = (id) => {
    setPage(0);
    setProductFilters((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const resetAllFilters = () => {
    setFamilyFilters([]);
    setBrandFilters([]);
    setProductFilters([]);
    setPriceMin('');
    setPriceMax('');
    setAppliedPriceMin('');
    setAppliedPriceMax('');
    setPage(0);
  };

  const activeFiltersCount =
    familyFilters.length + brandFilters.length + productFilters.length;

  const allBrands = (allBrandsRaw || []).map((b) => (typeof b === 'string' ? b : b.name));

  /* search-mode sidebar lists — level1FamilyId from backend maps directly to level-1 sidebar families */
  const searchBrands = (searchCounts?.brands || []).map((b) => b.name);
  const searchFamilyIds = new Set((searchCounts?.families || []).map((f) => f.id));
  const searchFamilies = allFamilies.filter((f) => searchFamilyIds.has(f.id));

  return (
    <Box sx={{ backgroundColor: '#F4F6FA', minHeight: '100vh' }}>
      <AddPanierModal />
      {location.pathname === '/' && <AnnouncementBanner />}
      <Box sx={{ px: { xs: '16px', md: '40px' }, py: '16px' }}>
        <Grid container spacing={0}>

          {/* ── Left sidebar ── */}
          <Grid item xs={12} md={2}>
            {/* Catalogue title above sidebar */}
            <Box sx={{ mb: '14px' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, ml: '8px', color: '#1A2035' }}>
                {search ? 'Résultats de recherche' : 'Catalogue'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#385576', mt: '2px', ml: '8px', fontFamily: 'Open Sans', fontWeight: 600 }}>
                {search ? 'Produits correspondants' : 'Explorez notre catalogue'}
              </Typography>
            </Box>

            <FilterSidebar
              search={search}
              familyFilters={familyFilters}
              toggleFamilyFilter={toggleFamilyFilter}
              brandFilters={brandFilters}
              toggleBrandFilter={toggleBrandFilter}
              productFilters={productFilters}
              toggleProductFilter={toggleProductFilter}
              allFamilies={search ? searchFamilies : allFamilies}
              allBrands={search ? searchBrands : allBrands}
              priceMin={priceMin}
              setPriceMin={setPriceMin}
              priceMax={priceMax}
              setPriceMax={setPriceMax}
              onApplyPrice={() => { setPage(0); setAppliedPriceMin(priceMin); setAppliedPriceMax(priceMax); }}
              resetAllFilters={resetAllFilters}
              filterCounts={search ? searchCounts : filterCounts}
              filterCountsLoading={search ? searchResultsLoading : filterCountsLoading}
            />
          </Grid>

          {/* ── Main content ── */}
          <Grid item xs={12} md={10} sx={{ pl: { md: '16px' } }}>
            {/* Results count + sort row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '16px',
                flexWrap: 'wrap',
                gap: '2px',
              }}
            >
              {/* Results count */}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#385576',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mt: '30px',
                  ml: '8px'
                }}
              >
                {(search ? searchResultsLoading : productsLoading)
                  ? <Skeleton variant="text" width={180} height={24} sx={{ display: 'inline-block' }} />
                  : <><Box component="span" sx={{ fontWeight: 700 }}>{totalElements.toLocaleString('fr-FR')}</Box> <Box component="span" sx={{ color: '#717182' }}>résultats trouvés</Box></>
                }
              </Typography>

              {/* Active filter chips + sort */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {brandFilters.map((b) => (
                  <Chip
                    key={b}
                    label={b}
                    size="small"
                    onDelete={() => toggleBrandFilter(b)}
                    sx={{ fontSize: '12px', fontWeight: 600, backgroundColor: '#EBF4FF', color: '#007BC6' }}
                  />
                ))}
                {/* Sort */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '32px' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    Trier par:
                  </Typography>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    variant="standard"
                    disableUnderline
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#007BC6',
                      '& .MuiSelect-select': { py: 0, pr: '24px !important' },
                      '& .MuiSelect-icon': { color: '#007BC6', fontSize: 20, top: 'calc(50% - 10px)' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: '1px solid #F7F7F7',
                          borderRadius: '12px',
                          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
                          mt: '6px',
                          p: '8px',
                          '& .MuiList-root': { p: 0 },
                        },
                      },
                    }}
                  >
                    {SORT_OPTIONS.map((opt, i) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{
                          fontSize: '14px',
                          fontWeight: sortBy === opt.value ? 600 : 400,
                          color: sortBy === opt.value ? '#007BC6' : '#385576',
                          fontFamily: "'Open Sans', sans-serif",
                          px: '8px',
                          py: '10px',
                          borderBottom: i < SORT_OPTIONS.length - 1 ? '1px solid #F1F5F9' : 'none',
                          borderRadius: 0,
                          '&:hover': { backgroundColor: '#F8FAFC' },
                          '&.Mui-selected': { backgroundColor: 'transparent' },
                          '&.Mui-selected:hover': { backgroundColor: '#F8FAFC' },
                        }}
                      >
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>

            {/* Product grid */}
            {search ? (
              <SearchResults
                search={search}
                searchVersion={searchVersion}
                onOpenDetail={handleOpenDetail}
                onAddToCart={handleAddToCart}
                onTotalChange={setTotalElements}
                onLoadingChange={setSearchResultsLoading}
                familyFilters={familyFilters}
                brandFilters={brandFilters}
                onSearchCountsChange={setSearchCounts}
                priceMin={appliedPriceMin}
                priceMax={appliedPriceMax}
                productFilters={productFilters}
              />
            ) : productsLoading ? (
              <Grid container spacing={2}>
                {[...Array(10)].map((_, i) => (
                  <Grid item xs={6} sm={4} md={2.4} key={i}>
                    <Box sx={{ backgroundColor: '#fff', border: '1.5px solid #D1D5DC', borderRadius: '12px', overflow: 'hidden', height: '422px' }}>
                      <Skeleton variant="rectangular" height={228} />
                      <Box sx={{ p: '12px 14px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '8px' }}>
                          <Skeleton variant="text" width={60} height={16} />
                          <Skeleton variant="text" width={80} height={16} />
                        </Box>
                        <Skeleton variant="text" width="90%" height={18} />
                        <Skeleton variant="text" width="70%" height={18} sx={{ mb: '6px' }} />
                        <Skeleton variant="text" width="50%" height={16} />
                        <Skeleton variant="text" width="40%" height={14} sx={{ mb: '8px' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <Box>
                            <Skeleton variant="text" width={100} height={28} />
                            <Skeleton variant="text" width={70} height={16} />
                          </Box>
                          <Skeleton variant="rectangular" width={34} height={34} sx={{ borderRadius: '8px' }} />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : products.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {products.map((product) => (
                    <Grid item xs={6} sm={4} md={2.4} key={product.reference}>
                      <ProductCard product={product} onClick={handleOpenDetail} onAddToCart={handleAddToCart} />
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: '42px' }}>
                    <Pagination
                      count={totalPages}
                      page={page + 1}
                      onChange={(_, val) => setPage(val - 1)}
                      color="primary"
                      shape="rounded"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '13px',
                          fontWeight: 600,
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                }}
              >
                <EmptyState
                  title="Aucun produit trouvé"
                  description={
                    search
                      ? `Aucun résultat pour « ${search} ». Essayez d'autres mots-clés ou réinitialisez les filtres.`
                      : "Aucun produit ne correspond aux filtres sélectionnés."
                  }
                  icon={<SearchOutlinedIcon sx={{ fontSize: 32 }} />}
                  action={
                    activeFiltersCount > 0 && (
                      <Box
                        onClick={resetAllFilters}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid #007BC6',
                          color: '#007BC6',
                          backgroundColor: '#EBF4FF',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <FilterAltOffIcon sx={{ fontSize: 16 }} />
                        Réinitialiser les filtres
                      </Box>
                    )
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

};

export default Catalog;
